import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../../components/BookingModal'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'
import FAQSection from '../../components/FAQSection'

export default function QueenElizabethGameDrives() {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const faqs = [
    { question: 'Which sector is best for game drives?', answer: 'Kasenyi Plains (north) for classic big game: lions, elephants, buffalo, antelopes with good sighting frequency. Ishasha (south) specifically for tree-climbing lions—more remote but spectacular when you find them.' },
    { question: 'What animals will I definitely see?', answer: 'Elephants, buffalo, Uganda kob, warthogs are almost guaranteed. Hippos certain if driving near Kazinga Channel. Lions highly likely (80%+) in Kasenyi during morning drives. Leopards rarer (20-30%) but possible.' },
    { question: 'Morning or afternoon drives better?', answer: 'Morning (06:00-10:00) best for predators and photography light, cooler temperatures mean more activity. Afternoon (15:00-19:00) offers golden-hour light and animals emerging as heat subsides.' },
    { question: 'Do I need a private vehicle?', answer: 'Private vehicles give you flexibility to linger at sightings, choose routes, and adjust timing. Shared/group vehicles are more economical but follow set routes and schedules. Contact us for options and pricing.' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1516426122078-c23e76319801?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Queen Elizabeth Game Drives
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Explore Uganda's most biodiverse park on a classic safari
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                </div>
                <span className="text-lg">(246 reviews)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-eye" className="w-4 h-4 mr-2" />
                Big Cat Territory
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-users" className="w-4 h-4 mr-2" />
                95+ Mammal Species
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-calendar-check" className="w-4 h-4 mr-2" />
                Free Cancellation
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Game Drive
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Description Section */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                Uganda's Premier Big Game Safari Destination
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Queen Elizabeth National Park sprawls across 1,978 square kilometers of stunning diversity—from savannah plains where elephants roam in herds, to swampy wetlands alive with hippos and buffalo, to acacia woodlands hiding leopards in their branches. As Uganda's most visited park with the highest biodiversity count (95 mammal species, 600+ bird species), this is where classic East African safari experiences happen against backdrops of the Rwenzori Mountains and volcanic crater lakes.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The park divides into distinct sectors, each offering unique wildlife experiences. The Kasenyi Plains in the northeast resemble classic Serengeti-style savannah—open grasslands where Uganda kob gather in huge numbers, attracting predators like lions, leopards, and spotted hyenas. Morning drives here often reward with lion sightings, as prides return from nocturnal hunts to rest in the shade.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The Ishasha sector in the south is famous for tree-climbing lions—a behavior exhibited by only two lion populations worldwide. These lions lounge in massive fig branches to escape heat and biting flies, their tawny bodies draped impossibly across limbs like giant house cats. Finding them requires patience and keen-eyed scanning of tree canopies, but the reward is simply magical.
                </p>
              </div>
            </section>

            {/* Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Sample Safari Itinerary
              </h2>
              <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 text-lg">
                  <strong>⚠️ Important Note:</strong> This itinerary is tentative and can be customized based on your preferences,
                  pickup location, and which park sector you visit. We can adjust to suit your needs.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { icon: 'fa-eye', title: 'Tree-Climbing Lions', description: 'Famous Ishasha lions lounging in fig branches—rare behavior worldwide' },
                  { icon: 'fa-users', title: '95 Mammal Species', description: "Uganda's highest biodiversity: elephants, buffalo, leopards, hippos, more" },
                  { icon: 'fa-mountain', title: 'Rwenzori Backdrop', description: 'Snow-capped peaks visible from northern sectors on clear days' },
                  { icon: 'fa-map', title: 'Multiple Ecosystems', description: 'Savannah, wetlands, woodland, crater lakes—diverse habitats in one park' },
                  { icon: 'fa-camera', title: 'Photography Paradise', description: 'Open landscapes, good light, cooperative subjects—ideal safari photography' },
                  { icon: 'fa-music', title: '600+ Bird Species', description: 'Exceptional birding: raptors, waterbirds, forest specialists throughout' }
                ].map((highlight, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                      <p className="text-gray-700">{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Morning Drive Timeline (Kasenyi)
              </h2>
              <div className="space-y-6">
                {[
                  { time: '06:00', title: 'Early Departure', desc: 'Leave lodge for Kasenyi Plains as dawn breaks—predator prime time.' },
                  { time: '06:45', title: 'Sunrise Safari Begins', desc: 'Enter park as animals become active, excellent soft light for photography.' },
                  { time: '07:30', title: 'Lion Pride Located', desc: 'Find lions returning from hunt, watch social interactions and feeding behavior.' },
                  { time: '08:30', title: 'Elephant Herds', desc: 'Encounter family groups with calves, observe feeding and trunk-wrestling play.' },
                  { time: '09:15', title: 'Antelope Plains', desc: 'Uganda kob, topi, waterbucks grazing; warthogs, buffalo, possible leopard sightings.' },
                  { time: '10:00', title: 'Return', desc: 'Exit park as heat increases and animals seek shade, return for breakfast.' }
                ].map((step, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-sm text-center leading-tight p-1">
                          {step.time}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                        <p className="text-gray-700">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQs */}
            <FAQSection faqs={faqs} title="Common Questions" />

            {/* Related Activities */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                More Queen Elizabeth Adventures
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/kazinga-boat-safari" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1575550590262-4f1f72a5163f?w=800"
                      alt="Kazinga Boat Safari"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Kazinga Boat Safari</h3>
                    <p className="text-gray-700 mb-4">Hippo channel cruise.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>

                <Link to="/activities/lion-tracking" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1546182990-dffeafbe841d?w=800"
                      alt="Lion Tracking"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Lion Tracking Experience</h3>
                    <p className="text-gray-700 mb-4">Join researchers in the field.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> 3-4 hours</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Year-round</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Activity Fee:</strong> Contact for current rates</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                    <span><strong>Difficulty:</strong> Easy</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                    <span><strong>Group:</strong> Private or Shared</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-user" className="w-6 h-6 text-safari-green" />
                    <span><strong>Min Age:</strong> All ages</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Game Drive
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Park entrance fees",
                    "Professional guide",
                    "4x4 Safari vehicle",
                    "Pop-up roof for viewing",
                    "Bottled water"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Tips for guide",
                    "Personal insurance",
                    "Meals (unless full day)",
                    "Accommodation"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Safety
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Vehicles:</strong> Regularly serviced.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Guides:</strong> First-aid trained.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Queen Elizabeth Game Drives"
      />
    </div>
  )
}
